import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  DialogActions,
  Divider,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Switch,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import useApi from 'hooks/useApi';
import { StepType } from './utilites/StepType';

interface RuleCondition {
  step: string;
  component?: string;
  field: string;
  expression: string;
  value: string;
}

interface Rule {
  id: string;
  title: string;
  currentStepUid?: string; // Current step that the rule belongs to
  step: string; // Condition step being evaluated
  component: string;
  field: string;
  expression: string;
  value: string;
  conditions?: RuleCondition[];
  conditionOperator?: 'and' | 'or';
  successAction: string;
  failureAction: string;
  enableLooping?: boolean;
  maxLoopIterations?: number;
  loopBackTo?: string;
  markIteration?: boolean;
  loopIterationLabelStep?: string;
  loopIterationLabelField?: string;
  firstIterationLabelStep?: string;
  firstIterationLabelField?: string;
  // Whose steps a loop/skip may add back or cancel. The step range itself comes
  // from the flow graph; this decides whose work inside that range the rule is
  // allowed to touch. Defaults to ALL, which is how loops behaved before this
  // field existed, so rules saved without it are unchanged.
  loopActorScopeMode?: 'ALL' | 'TRIGGER_STEP' | 'SELECTED';
  loopActorRoles?: string[];
  isStepRule?: boolean; // Whether this is a step block rule
  // Child transaction fields
  childTxnEnabled?: boolean;
  childTxnTeam?: string;
  childTxnTeamName?: string; // Store the team name from backend
  childData?: boolean;
  // Additional fields for display
  successActionTitle?: string;
  failureActionTitle?: string;
}

interface ConditionPopoverProps {
  open: boolean;
  onClose: () => void;
  addNodeAndConnect?: (sourceId: string, handleId: 'onCompleteStep' | 'onFailedStep' | 'noTitleEdge1' | 'noTitleEdge2', stepType: string, step: any) => void;
  handleUpdateFlow?: () => void;
  refreshFlow?: () => void;
  refreshRules?: () => void;
  selectedNode: any; 
  isStepRule?:boolean;
}

interface FlowStep {
  id: string;
  uid: string;
  title: string;
  stepType: string;
  component?: {
    uid?: string;
    name: string;
    fields: Array<{
      question: string;
      inputComponent: string;
      uid?: string;
      options?: string[];
      description?: string;
      resourceType?: string | null;
    }>;
  };
}

const EXPRESSIONS = [
  { value: '==', label: 'Equal to' },
  { value: '!=', label: 'Not equal to' },
  { value: '>', label: 'Greater than' },
  { value: '<', label: 'Less than' },
  { value: '>=', label: 'Greater than or equal' },
  { value: '<=', label: 'Less than or equal' },
];

const createEmptyCondition = (): RuleCondition => ({
  step: '',
  field: '',
  expression: '==',
  value: ''
});
 

const ConditionPopover: React.FC<ConditionPopoverProps> = ({ open, onClose, addNodeAndConnect, handleUpdateFlow, refreshFlow, refreshRules, selectedNode,isStepRule = false }) => {
  const { getFlowByTeamUid, createRuleByStep, addNewStep, showToast, getRulesByTeamUid, deleteRule, updateRule, getTeamByClient, getTeamDetailsByUid, getResourceTypes } = useApi();

  const currentNodeUid = selectedNode?.id || selectedNode?.uid;
  // console.log(currentNodeUid, 'currentNodeUid');
  const { teamUid } = useParams<{ teamUid: string }>();
  
  const [rules, setRules] = useState<Rule[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [isNewRule, setIsNewRule] = useState(false);
  const [flowSteps, setFlowSteps] = useState<FlowStep[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [ruleType, setRuleType] = useState<'standard' | 'stepBlock'>('standard');
  const [teamOptions, setTeamOptions] = useState<Array<{ uid: string; name: string, id: string }>>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  // Roles for the loop actor-scope selector. Stored as ids, the same convention
  // DefaultStepsSettings uses, because the backend compares them to FlowStep.actor.
  const [roleOptions, setRoleOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Form state for editing
  const [formData, setFormData] = useState({
    currentNodeUid: currentNodeUid,
    title: '',
    conditionOperator: '' as '' | 'and' | 'or',
    conditions: [createEmptyCondition()],
    successAction: '',
    failureAction: '',
    enableLooping: false,
    maxLoopIterations: 1,
    loopBackTo: '',
    markIteration: false,
    loopIterationLabelStep: '',
    loopIterationLabelField: '',
    firstIterationLabelStep: '',
    firstIterationLabelField: '',
    loopActorScopeMode: 'ALL' as 'ALL' | 'TRIGGER_STEP' | 'SELECTED',
    loopActorRoles: [] as string[],
    childTxnEnabled: false,
    childTxnTeam: '',
    childData: false
  });

  // Fetch existing rules when in view mode
  const fetchExistingRules = async () => {
    if (!teamUid) return;
    
    try {
      setLoading(true);
      // Use dropdown selection to determine API call
      const response = ruleType === 'stepBlock'
        ? await getRulesByTeamUid(teamUid, true)  // Only step block rules (isStepRule: true)
        : await getRulesByTeamUid(teamUid);       // Get all rules, will filter below
      
      // Transform API response to match our Rule interface
      const rulesArray = Array.isArray(response?.data) ? response.data : []; 
      
      // Filter rules based on rule type for standard rules
      const filteredRules = ruleType === 'standard' 
        ? rulesArray.filter((rule: any) => !rule.isStepRule) // Only non-step rules
        : rulesArray; // Step block rules already filtered by API
      
      const transformedRules: Rule[] = filteredRules.map((rule: any) => {
        
        // Debug: Log the raw rule data from backend
        if (rule.action?.childTxn) {
          console.log('🔍 Backend returned rule with childTxn:', {
            title: rule.title,
            childTxnData: rule.action.childTxn,
            fullAction: rule.action
          });
        }
        
        // Extract success and failure action information
        const successActionUid = rule.action?.success?.uid || rule.action?.success || '';
        const failureActionUid = rule.action?.failure?.uid || rule.action?.failure || '';
        const successActionTitle = rule.action?.success?.title || '';
        const failureActionTitle = rule.action?.failure?.title || '';
 
        // Resolve team name from teamOptions if available
        let childTxnTeamName = rule.action?.childTxn?.teamName || '';
        if (rule.action?.childTxn?.team && !childTxnTeamName) {
          const matchingTeam = teamOptions.find(t => t.uid === rule.action.childTxn.team);
          if (matchingTeam) {
            childTxnTeamName = matchingTeam.name;
          }
        }

        console.log('childTxn.team raw value:', rule.action?.childTxn?.team, typeof rule.action?.childTxn?.team);
        
        const normalizedConditions: RuleCondition[] = Array.isArray(rule.conditions)
          ? rule.conditions.map((condition: any) => ({
              step: condition.step?.uid || condition.step || '',
              component: condition.component?.uid || condition.component || '',
              field: condition.field || '',
              expression: condition.expression || '==',
              value: condition.value || ''
            }))
          : [];
        const primaryCondition = normalizedConditions[0];
        const operatorValue = (rule.conditionOperator || '').toLowerCase();
        const conditionOperator = operatorValue === 'or' ? 'or' : operatorValue === 'and' ? 'and' : undefined;

        return {
          id: rule.uid,
          title: rule.title,
          // Extract current step UID (rule's own step) - this is for CurrentStep Name dropdown
          currentStepUid: rule.step?.uid || 'N/A',
          // Extract condition step UID (step being evaluated) - this is for Step Name dropdown
          step: primaryCondition?.step || 'N/A',
          // Updated to extract uid from nested component object
          component: primaryCondition?.component || '',
          field: primaryCondition?.field || '',
          expression: primaryCondition?.expression || '==',
          value: primaryCondition?.value || '',
          conditions: normalizedConditions,
          conditionOperator: conditionOperator,
          // Store UID for API calls
          successAction: successActionUid,
          failureAction: failureActionUid,
          enableLooping: rule.action?.enableLooping || false,
          maxLoopIterations: rule.action?.maxLoopIterations || 1,
          loopBackTo: rule.action?.loopBackTo || rule.action?.loopBackToStep || '',
          markIteration: rule.action?.markIteration || false,
          loopIterationLabelStep: rule.action?.loopIterationLabelStep?.uid || rule.action?.loopIterationLabelStep || '',
          loopIterationLabelField: rule.action?.loopIterationLabelField || '',
          firstIterationLabelStep: rule.action?.firstIterationLabelStep?.uid || rule.action?.firstIterationLabelStep || '',
          firstIterationLabelField: rule.action?.firstIterationLabelField || '',
          loopActorScopeMode: (rule.action?.loopActorScopeMode || 'ALL') as 'ALL' | 'TRIGGER_STEP' | 'SELECTED',
          // Backend returns these populated. Take `uid` first: roleOptions are keyed by
          // uid (resource-types returns `id: uid`), while a populated doc's `id`/`_id`
          // is the ObjectId and would never match an option.
          loopActorRoles: Array.isArray(rule.action?.loopActorRoles)
            ? rule.action.loopActorRoles.map((r: any) => String(r?.uid || r?.id || r?._id || r)).filter(Boolean)
            : [],
          isStepRule: rule.isStepRule || false, // Track if this is a step block rule
          // Child transaction fields
          childTxnEnabled: rule.action?.childTxn?.enabled || false,
         childTxnTeam: (() => {
          const rawTeam = rule.action?.childTxn?.team || '';
          // Backend stores MongoDB id, find the matching uid from teamOptions
          const matchedTeam = teamOptions.find(t => t.uid === rawTeam || (t as any).id === rawTeam);
          return matchedTeam ? matchedTeam.uid : rawTeam; // Always return uuid
        })(),
          childTxnTeamName: childTxnTeamName, // Resolved from teamOptions or backend
          childData: rule.action?.childTxn?.childData || false,
          // Store titles for display purposes
          successActionTitle: successActionTitle || 'N/A',
          failureActionTitle: failureActionTitle || 'N/A'
        };
      }); 
      setRules(transformedRules);
    } catch (error) {
      console.error('Error fetching existing rules:', error);
      setRules([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch flow data when dialog opens
  useEffect(() => {
    if (open && teamUid) {
      fetchFlowData();
    }
  }, [open, teamUid]);

  // Fetch existing rules after flow data is loaded
  useEffect(() => {
    if (open && teamUid && flowSteps.length > 0 && !loadingTeams) {
      fetchExistingRules();
    }
  }, [open, teamUid, flowSteps.length, ruleType, teamOptions, loadingTeams]);


  const fetchFlowData = async () => {
    try {
      setLoading(true);
      const response = await getFlowByTeamUid(teamUid!);
      if (response.success && response.data.steps) {
        setFlowSteps(response.data.steps);
      }
    } catch (error) {
      console.error('Failed to fetch flow data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch teams for child transaction dropdown
  const fetchTeamsForChildTxn = async () => {
    if (!teamUid) return;
    
    try {
      setLoadingTeams(true);
      // First get the team details to find the client ID
      const teamDetails = await getTeamDetailsByUid(teamUid); 
      // The client UID is at data.client.uid
      if (teamDetails?.success && teamDetails?.data?.client?.uid) {
        const clientId = teamDetails.data.client.uid; 

        // Roles for the loop actor-scope selector. Fire-and-forget: a failure here
        // must not stop the team list loading.
        setLoadingRoles(true);
        getResourceTypes(clientId, 'user')
          .then((res: any) => {
            const list = Array.isArray(res?.data) ? res.data : [];
            setRoleOptions(
              list
                .map((r: any) => ({ id: String(r?.id || r?._id || ''), name: r?.name || '' }))
                .filter((r: any) => r.id && r.id !== 'undefined')
            );
          })
          .catch(() => setRoleOptions([]))
          .finally(() => setLoadingRoles(false));

        // Then get all teams for that client
        const teamsResponse = await getTeamByClient(clientId);
         console.log(teamsResponse,"filteredTeamsfilteredTeamsfilteredTeams")
        if (teamsResponse?.data) {
          // Keep all teams including the current one
          setTeamOptions(teamsResponse.data);
        }
      } else {
        console.log('Could not find client in team details:', teamDetails?.data);
      }
    } catch (error) {
      console.error('Failed to fetch teams for child transaction:', error);
      setTeamOptions([]);
    } finally {
      setLoadingTeams(false);
    }
  };

  // Fetch teams when dialog opens
  useEffect(() => {
    if (open && teamUid) {
      fetchTeamsForChildTxn();
      
    }
  }, [open, teamUid]);
  
  

  function toSentenceCase(input: string): string {
    const quotedMap: Record<number, string> = {};
    let idx = 0;

    // Step 1: Replace "quoted" text with lowercase placeholders
    //         Store the UPPERCASED version in quotedMap
    const withPlaceholders = input.replace(
      /"([^"]*)"/g,
      (_: string, inner: string): string => {
        const key = `__q${idx}__`;
        quotedMap[idx] = inner.toUpperCase();
        idx++;
        return key;
      }
    );

    // Step 2: Lowercase everything, then capitalize only the first char
    const sentenceCased = withPlaceholders
      .toLowerCase()
      .replace(/^\w/, (c: string): string => c.toUpperCase());

    // Step 3: Restore placeholders with their UPPERCASE values
    return sentenceCased.replace(
      /__q(\d+)__/g,
      (_: string, i: string): string => quotedMap[Number(i)]
    );
  }

  const getFieldsForStep = (stepUid: string) => {
    const selectedStep = flowSteps.find(step => step.uid === stepUid);
    return selectedStep?.component?.fields || [];
  };

  // Step numbers here must match the numbers drawn on the flow canvas, which are the
  // 1-based positions of flowData.steps (see quikflow/index.tsx loadFlow).
  const getStepNumber = (stepUid: string) => {
    const index = flowSteps.findIndex(step => step.uid === stepUid);
    return index >= 0 ? index + 1 : null;
  };

  const getStepLabel = (step: FlowStep, index?: number) => {
    const stepNumber = typeof index === 'number' ? index + 1 : getStepNumber(step.uid);
    return stepNumber ? `${stepNumber}. ${step.title}` : step.title;
  };

  const updateConditionAtIndex = (index: number, updates: Partial<RuleCondition>) => {
    setFormData((prev) => {
      const nextConditions = prev.conditions.map((condition, conditionIndex) =>
        conditionIndex === index ? { ...condition, ...updates } : condition
      );

      return {
        ...prev,
        conditions: nextConditions
      };
    });
  };

  const handleConditionStepChange = (index: number, stepUid: string) => {
    updateConditionAtIndex(index, {
      step: stepUid,
      field: '',
      value: ''
    });
  };

  const handleConditionFieldChange = (index: number, fieldValue: string) => {
    updateConditionAtIndex(index, {
      field: fieldValue,
      value: ''
    });
  };

  const handleAddCondition = () => {
    setFormData((prev) => ({
      ...prev,
      conditions: [...prev.conditions, createEmptyCondition()]
    }));
  };

  const handleRemoveCondition = (index: number) => {
    setFormData((prev) => {
      if (prev.conditions.length <= 1) {
        return prev;
      }

      const nextConditions = prev.conditions.filter((_, conditionIndex) => conditionIndex !== index);

      return {
        ...prev,
        conditions: nextConditions,
        conditionOperator: nextConditions.length <= 1 ? '' : prev.conditionOperator
      };
    });
  };

  const handleConditionOperatorSelect = (operator: 'and' | 'or') => {
    setFormData((prev) => {
      const shouldAddCondition = prev.conditions.length < 2;

      return {
        ...prev,
        conditionOperator: operator,
        conditions: shouldAddCondition
          ? [...prev.conditions, createEmptyCondition()]
          : prev.conditions
      };
    });
  };

  const handleAddRule = () => {
     
    
    setFormData({
      currentNodeUid: currentNodeUid,
      title: '',
      conditionOperator: '' as '' | 'and' | 'or',
      conditions: [createEmptyCondition()],
      successAction: '',
      failureAction: '',
      enableLooping: ruleType !== 'stepBlock', // Use ruleType instead of isStepRule prop
      maxLoopIterations: 500000,
      loopBackTo: '',
      markIteration: false,
      loopIterationLabelStep: '',
      loopIterationLabelField: '',
      firstIterationLabelStep: '',
      firstIterationLabelField: '',
      // New rules state the scope explicitly rather than inheriting the schema
      // default, so the author chooses instead of getting inferred behaviour.
      loopActorScopeMode: 'ALL' as 'ALL' | 'TRIGGER_STEP' | 'SELECTED',
      loopActorRoles: [] as string[],
      childTxnEnabled: false,
      childTxnTeam: '',
      childData: false
    });
    setEditingRule(null);
    setIsNewRule(true); // CRITICAL: Ensure this is true for new rules
    setEditDialogOpen(true); 
  };

  const handleEditRule = (rule: Rule) => {
    console.log('🔧 EDIT MODE: Opening rule for editing', rule);

    const normalizedConditions = rule.conditions && rule.conditions.length > 0
      ? rule.conditions.map((condition) => ({
          step: condition.step === 'N/A' ? '' : condition.step,
          field: condition.field || '',
          expression: condition.expression || '==',
          value: condition.value || ''
        }))
      : [
          rule.step && rule.step !== 'N/A'
            ? {
                step: rule.step,
                field: rule.field || '',
                expression: rule.expression || '==',
                value: rule.value || ''
              }
            : createEmptyCondition()
        ];
    const conditionOperator: '' | 'and' | 'or' = rule.conditionOperator
      ? rule.conditionOperator
      : (normalizedConditions.length > 1 ? 'and' : '');
    
    // First set the form data - use UIDs for form values
    setFormData({
      currentNodeUid: rule.currentStepUid === 'N/A' ? '' : rule.currentStepUid, // ✅ CurrentStep Name uses rule's own step UID
      title: rule.title,
      conditionOperator: conditionOperator,
      conditions: normalizedConditions,
      // Use UIDs for dropdown values - ensure empty string if no valid UID
      successAction: rule.successAction && rule.successAction !== 'N/A' ? rule.successAction : '',
      failureAction: rule.failureAction && rule.failureAction !== 'N/A' ? rule.failureAction : '',
      enableLooping: rule.enableLooping || false,
      maxLoopIterations: rule.maxLoopIterations || 1,
      loopBackTo: (() => {
        const loopBackTo = rule.loopBackTo as any;
        return (typeof loopBackTo === 'object' && loopBackTo?.uid) ? loopBackTo.uid : (loopBackTo || '');
      })(),
      markIteration: rule.markIteration || false,
      loopIterationLabelStep: (() => {
        const s = rule.loopIterationLabelStep as any;
        return (typeof s === 'object' && s?.uid) ? s.uid : (s || '');
      })(),
      loopIterationLabelField: rule.loopIterationLabelField || '',
      firstIterationLabelStep: (() => {
        const s = rule.firstIterationLabelStep as any;
        return (typeof s === 'object' && s?.uid) ? s.uid : (s || '');
      })(),
      firstIterationLabelField: rule.firstIterationLabelField || '',
      loopActorScopeMode: (rule.loopActorScopeMode || 'ALL') as 'ALL' | 'TRIGGER_STEP' | 'SELECTED',
      loopActorRoles: rule.loopActorRoles || [],
      childTxnEnabled: rule.childTxnEnabled || false,
      childTxnTeam: rule.childTxnTeam || '',
      childData: rule.childData || false
    });

    if(rule.childTxnTeam && !teamOptions.find(t => t.uid === rule.childTxnTeam)) {
      setTeamOptions(prev => [...prev, { uid: rule.childTxnTeam!, name: rule.childTxnTeamName || 'Unknown Team' ,id:''}]);  
    }
    
    setEditingRule(rule);
    setIsNewRule(false); // CRITICAL: Ensure this is false for updates
    setEditDialogOpen(true);  
  };

  const handleDeleteRule = async (ruleId: string, ruleTitle: string) => {
    // Show confirmation dialog
    const confirmed = window.confirm(`Are you sure you want to delete the rule "${ruleTitle}"? This action cannot be undone.`);
    
    if (!confirmed) return;

    try {
      // Call the delete API
      const response = await deleteRule(ruleId);
      
      if (response.success) {
        // Remove from local state
        setRules(rules.filter(rule => rule.id !== ruleId));
        showToast('Rule deleted successfully!', 'success');
        
        // Refresh the rules list from backend to ensure consistency
        if (flowSteps.length > 0) {
          await fetchExistingRules();
        }
        
        // Reload the entire flow to remove loop edges
        if (refreshFlow) { 
          setTimeout(() => {
            refreshFlow();
          }, 300);
        }

        refreshRules?.();
      } else {
        throw new Error(response.message || 'Failed to delete rule');
      }
    } catch (error) {
      console.error('Error deleting rule:', error);
      showToast('Failed to delete rule. Please try again.', 'error');
    }
  };

  const handleSaveRule = async () => {
    if (!teamUid) {
      showToast('Team UID is required', 'error');
      return;
    } 
    setSavingRule(true);
    try {
      if (isNewRule) {
        // Create new rule and apply it immediately
        const primaryCondition = formData.conditions[0] || createEmptyCondition();
        const operatorToSave = (formData.conditionOperator || (formData.conditions.length > 1 ? 'and' : undefined)) as
          | 'and'
          | 'or'
          | undefined;
        const newRule: Rule = {
          id: Date.now().toString(),
          title: formData.title,
          currentStepUid: formData.currentNodeUid || '', // Current step that rule belongs to
          step: primaryCondition.step || '', // Condition step being evaluated
          component: primaryCondition.component || '', // Allow empty component
          field: primaryCondition.field || '', // Allow empty field
          expression: primaryCondition.expression || '==', // Default expression
          value: primaryCondition.value || '', // Allow empty value
          conditions: formData.conditions,
          conditionOperator: operatorToSave,
          successAction: formData.successAction || '', // Allow empty success action
          failureAction: formData.failureAction || '', // Allow empty failure action
          enableLooping: formData.enableLooping,
          maxLoopIterations: formData.maxLoopIterations,
          loopBackTo: formData.loopBackTo || '',
          markIteration: formData.markIteration || false,
          loopIterationLabelStep: formData.loopIterationLabelStep || '',
          loopIterationLabelField: formData.loopIterationLabelField || '',
          firstIterationLabelStep: formData.firstIterationLabelStep || '',
          firstIterationLabelField: formData.firstIterationLabelField || '',
          loopActorScopeMode: formData.loopActorScopeMode || 'ALL',
          loopActorRoles: formData.loopActorRoles || [],
          isStepRule: ruleType === 'stepBlock', // Add isStepRule for immediate display
          childTxnEnabled: formData.childTxnEnabled,
          childTxnTeam: formData.childTxnTeam || editingRule?.childTxnTeam || '',
          childTxnTeamName: (() => {
            const team = teamOptions.find(t => t.uid === formData.childTxnTeam); 
            return team ? team.name : '';
          })(),
          childData: formData.childData
        }; 

        // Apply the rule immediately to backend (CREATE)
        await applyRuleToBackend(newRule, false); // false = not an update
        
        // Refresh rules from backend to get the actual UID
        await fetchExistingRules();
        refreshRules?.();
        showToast('Rule created and applied successfully!', 'success');
        
      } else if (editingRule && !isNewRule) { 
        
        // Create updated rule object with changes
        const primaryCondition = formData.conditions[0] || createEmptyCondition();
        const operatorToSave = (formData.conditionOperator || (formData.conditions.length > 1 ? 'and' : undefined)) as
          | 'and'
          | 'or'
          | undefined;
        const updatedRule: Rule = {
          ...editingRule, 
          // Only update fields that have been changed/filled
          ...(formData.title && { title: formData.title }),
          ...(formData.currentNodeUid && { currentStepUid: formData.currentNodeUid }),
          step: primaryCondition.step || '',
          component: primaryCondition.component || '',
          field: primaryCondition.field || '',
          expression: primaryCondition.expression || '==',
          value: primaryCondition.value || '',
          conditions: formData.conditions,
          conditionOperator: operatorToSave,
          ...(formData.successAction && { successAction: formData.successAction }),
          ...(formData.failureAction && { failureAction: formData.failureAction }),
          // Always update boolean and number fields as they have valid falsy values
          enableLooping: formData.enableLooping,
          maxLoopIterations: formData.maxLoopIterations,
          ...(formData.loopBackTo && { loopBackTo: formData.loopBackTo }),
          markIteration: formData.markIteration || false,
          loopIterationLabelStep: formData.loopIterationLabelStep || '',
          loopIterationLabelField: formData.loopIterationLabelField || '',
          firstIterationLabelStep: formData.firstIterationLabelStep || '',
          firstIterationLabelField: formData.firstIterationLabelField || '',
          loopActorScopeMode: formData.loopActorScopeMode || 'ALL',
          loopActorRoles: formData.loopActorRoles || [],
          // Child transaction fields
          childTxnEnabled: formData.childTxnEnabled,
          childTxnTeam: formData.childTxnTeam || '',
          childTxnTeamName: (() => {
            const team = teamOptions.find(t => t.uid === formData.childTxnTeam);
            return team ? team.name : (editingRule.childTxnTeamName || '');
          })(),
          childData: formData.childData
        };

         
        
        // Apply the updated rule to backend (UPDATE)
        await applyRuleToBackend(updatedRule, true); // true = this is an update
        refreshRules?.();
        
        showToast('Rule updated and applied successfully!', 'success');
      } else {
        console.error('Invalid state: neither new rule nor existing rule update');
        showToast('Invalid operation state', 'error');
        return;
      }
      
      handleCloseEditDialog();
    } catch (error) {
      console.error('Error saving rule:', error);
      showToast('Failed to save rule', 'error');
    } finally {
      setSavingRule(false);
    }
  };

  // Helper function to apply single rule to backend
  const applyRuleToBackend = async (rule: Rule, isUpdate: boolean = false) => {
    try {
      const operatorValue = (rule.conditionOperator || formData.conditionOperator || 'and').toLowerCase();
      const conditionOperator = operatorValue === 'or' ? 'or' : 'and';
      const sourceConditions: RuleCondition[] = rule.conditions && rule.conditions.length > 0
        ? rule.conditions
        : (rule.step || rule.field ? [{
            step: rule.step === 'N/A' ? '' : rule.step,
            component: rule.component || '',
            field: rule.field || '',
            expression: rule.expression || '==',
            value: rule.value || ''
          }] : []);
      const conditionsPayload = sourceConditions.reduce((acc: Array<any>, condition) => {
        if (!condition.step || !condition.field) {
          return acc;
        }

        const stepDetails = flowSteps.find(step => step.uid === condition.step);
        if (!stepDetails) {
          throw new Error(`Condition step not found for rule: ${rule.title}`);
        }

        acc.push({
          step: condition.step,
          component: stepDetails.component?.uid || stepDetails.id,
          field: condition.field,
          expression: condition.expression || '==',
          value: condition.value || ''
        });

        return acc;
      }, []);
      const hasConditions = conditionsPayload.length > 0;
      
      // For updates, use simplified payload structure
      if (isUpdate) {
      
        
        const updatePayload = {
          title: rule.title,
          currentNodeStepUid: formData.currentNodeUid,
          isStepRule: ruleType === 'stepBlock', // Use dropdown selection instead of prop
          conditionOperator: conditionOperator,
          conditions: conditionsPayload,
          action: {
            // ✅ ALWAYS include success and failure - use UIDs for existing steps
            success: rule.successAction && rule.successAction !== 'N/A' ? rule.successAction : null,
            failure: rule.failureAction && rule.failureAction !== 'N/A' ? rule.failureAction : null,
            // Include loop configuration
            enableLooping: rule.enableLooping || false,
            maxLoopIterations: rule.maxLoopIterations || 1,
            ...(rule.enableLooping && rule.loopBackTo ? {
              loopBackTo: rule.loopBackTo,
              loopBackToStep: rule.loopBackTo
            } : {}),
            ...(rule.enableLooping ? {
              markIteration: rule.markIteration || false,
              ...(rule.markIteration && rule.loopIterationLabelStep ? { loopIterationLabelStep: rule.loopIterationLabelStep } : {}),
              ...(rule.markIteration && rule.loopIterationLabelField ? { loopIterationLabelField: rule.loopIterationLabelField } : {}),
              ...(rule.markIteration && rule.firstIterationLabelStep ? { firstIterationLabelStep: rule.firstIterationLabelStep } : {}),
              ...(rule.markIteration && rule.firstIterationLabelField ? { firstIterationLabelField: rule.firstIterationLabelField } : {}),
              loopActorScopeMode: rule.loopActorScopeMode || 'ALL',
              loopActorRoles: rule.loopActorScopeMode === 'SELECTED' ? (rule.loopActorRoles || []) : [],
            } : {}),
            // Include child transaction configuration
            ...(rule.childTxnEnabled ? (() => {
              const team = teamOptions.find(t => t.uid === rule.childTxnTeam);
              const childTxnPayload = {
                enabled: true,
                team: formData.childTxnTeam || rule.childTxnTeam,
                teamName: team ? team.name : (rule.childTxnTeamName || ''),
                childData: formData.childData
              };
              console.log('📤 UPDATE - Child transaction payload:', childTxnPayload);

              return { childTxn: childTxnPayload };
            })() : {})
          }
        };
        
        // Use the proper updateRule API for updates
        const response = await updateRule(rule.id, updatePayload); 
        
        if (response.success) { 
          
          // Trigger flow update for visual sync
          if (handleUpdateFlow) { 
            setTimeout(() => {
              handleUpdateFlow();
            }, 500);
          }
          
          // Refresh flow data to show changes
          await fetchFlowData();
          // Refresh rules to get the latest data
          await fetchExistingRules();
        } else {
          throw new Error(`Failed to update rule "${rule.title}": ${response.message || 'Unknown error'}`);
        }
        
        return; // Exit early for updates
      } 
      
      // If no valid conditions are provided, this might be a loop-only rule
      if (!hasConditions) { 
        
        // Create a minimal rule with just loop configuration
        const loopOnlyPayload = {
          title: rule.title,
          isStepRule: ruleType === 'stepBlock', // Use dropdown selection instead of prop
          conditionOperator: conditionOperator,
          conditions: [], // Empty conditions array
          action: {
            enableLooping: rule.enableLooping || false,
            maxLoopIterations: rule.maxLoopIterations || 1,
            ...(rule.enableLooping && rule.loopBackTo ? {
              loopBackTo: rule.loopBackTo,
              loopBackToStep: rule.loopBackTo
            } : {}),
            ...(rule.enableLooping ? {
              markIteration: rule.markIteration || false,
              ...(rule.markIteration && rule.loopIterationLabelStep ? { loopIterationLabelStep: rule.loopIterationLabelStep } : {}),
              ...(rule.markIteration && rule.loopIterationLabelField ? { loopIterationLabelField: rule.loopIterationLabelField } : {}),
              ...(rule.markIteration && rule.firstIterationLabelStep ? { firstIterationLabelStep: rule.firstIterationLabelStep } : {}),
              ...(rule.markIteration && rule.firstIterationLabelField ? { firstIterationLabelField: rule.firstIterationLabelField } : {}),
              loopActorScopeMode: rule.loopActorScopeMode || 'ALL',
              loopActorRoles: rule.loopActorScopeMode === 'SELECTED' ? (rule.loopActorRoles || []) : [],
            } : {})
            // Intentionally omit success and failure for loop-only rules
          }
        };

        console.log('Creating loop-only rule with payload:', loopOnlyPayload);
        
        const response = await createRuleByStep(formData.currentNodeUid, loopOnlyPayload);
        
        if (response.success) { 
          
          // Trigger flow update for visual sync
          if (handleUpdateFlow) { 
            setTimeout(() => {
              handleUpdateFlow();
              
            }, 500);
          } else {
            console.warn('⚠️ handleUpdateFlow is not provided - visual sync skipped for loop-only rule');
          }
          
          await fetchFlowData(); 
        } else {
          throw new Error(`Failed to create loop-only rule "${rule.title}": ${response.message || 'Unknown error'}`);
        }
        
        return; // Exit early for loop-only rules
      }
      
      let successStepUid = null;
      let failureStepUid = null;
      
      // Check if success/failure actions are step types or existing step UIDs
      const isSuccessActionStepType = Object.values(StepType).includes(rule.successAction as StepType);
      const isFailureActionStepType = Object.values(StepType).includes(rule.failureAction as StepType);
      
      // Handle case where no success/failure actions are specified
      const hasSuccessAction = rule.successAction && rule.successAction.trim() !== '';
      const hasFailureAction = rule.failureAction && rule.failureAction.trim() !== '';
      
      // Step 1: Handle success action only if provided
      if (hasSuccessAction) {
        if (isSuccessActionStepType) {
          // Create new success step
          const successStepPayload = {
            currentNode: {
              title: rule.successAction,
              stepType: rule.successAction,
              // change the perent rule.step to current node uid
              parents: [formData.currentNodeUid],
              dependency: []
            },
            parentNode: {
              nodeKey: 'onCompleteStep' as const
            }
          }; 
          const successResponse = await addNewStep(teamUid!, successStepPayload);
          
          if (successResponse.success && successResponse.data?.uid) {
            successStepUid = successResponse.data.uid; 
            
            if (addNodeAndConnect) {
              addNodeAndConnect(formData.currentNodeUid, 'onCompleteStep', rule.successAction, successResponse.data);
            }
          } else {
            throw new Error('Failed to create success step');
          }
        } else {
          // Use existing step UID
          successStepUid = rule.successAction; 
        }
      } else {
        console.log('No success action specified - skipping success step creation');
      }

      // Step 2: Handle failure action only if provided
      if (hasFailureAction) {
        if (isFailureActionStepType) {
          // Create new failure step
          const failureStepPayload = {
            currentNode: {
              title: rule.failureAction,
              stepType: rule.failureAction,
              // parents: [rule.step],
              parents: [formData.currentNodeUid],
              dependency: []
            },
            parentNode: {
              nodeKey: 'onFailedStep' as const
            }
          }; 
          const failureResponse = await addNewStep(teamUid!, failureStepPayload);
          
          if (failureResponse.success && failureResponse.data?.uid) {
            failureStepUid = failureResponse.data.uid; 
            
            if (addNodeAndConnect) {
              addNodeAndConnect(formData.currentNodeUid, 'onFailedStep', rule.failureAction, failureResponse.data);
            }
          } else {
            throw new Error('Failed to create failure step');
          }
        } else {
          // Use existing step UID
          failureStepUid = rule.failureAction; 
        }
      } else {
        console.log('No failure action specified - skipping failure step creation');
      }

      // Step 3: Create the rule with the step UIDs
      // Check if we have any actions or looping configuration
      const hasActions = successStepUid || failureStepUid;
      const hasLooping = rule.enableLooping;
      const hasChildTxn = rule.childTxnEnabled;
      const needsActionObject = hasActions || hasLooping || hasChildTxn;
      
      const rulePayload = {
        title: rule.title,
        currentNodeStepUid: formData.currentNodeUid,
        isStepRule: ruleType === 'stepBlock', // Use dropdown selection instead of prop
        conditionOperator: conditionOperator,
        conditions: conditionsPayload, // Empty conditions if no valid entries
        // Only include action object if we actually need it
        ...(needsActionObject ? {
          action: {
            // Only include success/failure if they exist
            ...(successStepUid && { success: successStepUid }),
            ...(failureStepUid && { failure: failureStepUid }),
            // Include looping configuration if enabled
            ...(hasLooping ? {
              enableLooping: rule.enableLooping,
              maxLoopIterations: rule.maxLoopIterations || 1,
              ...(rule.loopBackTo ? {
                loopBackTo: rule.loopBackTo,
                loopBackToStep: rule.loopBackTo
              } : {}),
              markIteration: rule.markIteration || false,
              ...(rule.markIteration && rule.loopIterationLabelStep ? { loopIterationLabelStep: rule.loopIterationLabelStep } : {}),
              ...(rule.markIteration && rule.loopIterationLabelField ? { loopIterationLabelField: rule.loopIterationLabelField } : {}),
              ...(rule.markIteration && rule.firstIterationLabelStep ? { firstIterationLabelStep: rule.firstIterationLabelStep } : {}),
              ...(rule.markIteration && rule.firstIterationLabelField ? { firstIterationLabelField: rule.firstIterationLabelField } : {}),
              loopActorScopeMode: rule.loopActorScopeMode || 'ALL',
              loopActorRoles: rule.loopActorScopeMode === 'SELECTED' ? (rule.loopActorRoles || []) : [],
            } : {}),
            // Include child transaction configuration if enabled
            ...(hasChildTxn ? (() => {
              const team = teamOptions.find(t => t.uid === rule.childTxnTeam);
              const childTxnPayload = {
                enabled: true,
                team: rule.childTxnTeam,
                teamName: team ? team.name : '',
                childData: rule.childData || false
              };
              console.log('📤 CREATE - Child transaction payload:', childTxnPayload);
              return { childTxn: childTxnPayload };
            })() : {})
          }
        } : {})
      };
 
      
      // Create rule using the step UID\
      //change it from rule.step to formdata.currentnodeuid
      const response = await createRuleByStep(formData.currentNodeUid, rulePayload); 
      
      if (response.success) {
        console.log(`Rule "${rule.title}" created successfully`);
        
        // Trigger flow update for visual sync
        if (handleUpdateFlow) {
          console.log('Triggering flow update for visual sync...');
          setTimeout(() => {
            handleUpdateFlow();
            console.log(`calling updateflow after creating rule and this is second api  ${rule.title}`);
          }, 500);
        } else {
          console.warn('⚠️ handleUpdateFlow is not provided - visual sync skipped');
        }
        
        // Refresh flow data to show new steps
        await fetchFlowData();
        console.log('Flow data refreshed after creating rule second api', rule.title);
      } else {
        throw new Error(`Failed to create rule "${rule.title}": ${response.message || 'Unknown error'}`);
      }
      
    } catch (error) {
      console.error(`Error applying rule "${rule.title}":`, error);
      throw error; // Re-throw to be caught by handleSaveRule
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
  };

  const handleInputChange = (field: string) => (event: any) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  return (
    <>
      {/* Main Condition Dialog */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: '500px'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Rules
            </Typography>  
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Rule Type</InputLabel>
                <Select
                  value={ruleType}
                  onChange={(e) => {
                    setRuleType(e.target.value as 'standard' | 'stepBlock');
                    // useEffect will handle refetch when ruleType changes
                  }}
                  label="Rule Type"
                >
                  <MenuItem value="standard">Standard rule</MenuItem>
                  <MenuItem value="stepBlock">Step block rule</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                // startIcon={<AddIcon />}
                onClick={handleAddRule}
                size="small"
                sx={{ borderRadius: 1, px: 2 }}
              >
                Add Rule
              </Button>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress size={40} /> 
              
            </Box>
          ) : rules.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No Rule Configured
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                Add your first Rule to get started with conditional logic
              </Typography>
              <Button
                variant="outlined"
                // startIcon={<AddIcon />}
                onClick={handleAddRule}
                sx={{ borderRadius: 1, px: 2 }}
              >
                Create Your First Rule
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Table>
                <TableHead sx={{ backgroundColor: 'grey.50' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Condition Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Step</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Condition</TableCell>
                    <TableCell sx={{ fontWeight: 600, padding: 0 }}>Actions</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Edit/Delete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map((rule, index) => (
                    <TableRow key={rule.id} hover sx={{ '& td, & th': { verticalAlign: 'top' } }}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {rule.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                          {(() => {
                            const step = flowSteps.find(step => step.uid === rule.currentStepUid);
                            if (step) {
                              const stepIndex = flowSteps.findIndex(s => s.uid === step.uid);
                              return (
                                <>
                                  <span style={{ fontWeight: 500 }}>
                                    Step No : {stepIndex + 1}
                                    <br />
                                    {step.title}
                                  </span>
                                </>
                              )
                            }
                            return 'N/A';
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'left', verticalAlign: 'top' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start', textAlign: 'left', width: '100%' }}>
                          {(() => {
                            const conditionsToDisplay = rule.conditions && rule.conditions.length > 0
                              ? rule.conditions
                              : [{
                                  step: rule.step,
                                  component: rule.component,
                                  field: rule.field,
                                  expression: rule.expression,
                                  value: rule.value
                                }];
                            const operatorLabel = (rule.conditionOperator || '').toUpperCase();
                            const showOperator = conditionsToDisplay.length > 1 && operatorLabel;

                            return conditionsToDisplay.map((condition, conditionIndex) => {
                              const conditionStep = flowSteps.find(step => step.uid === condition.step);
                              const field = conditionStep?.component?.fields?.find((f: any) =>
                                (f.uid || f.question) === condition.field
                              );

                              return (
                                <Box key={`${condition.step || 'condition'}-${conditionIndex}`}>
                                  {conditionStep && (
                                    <Typography
                                      component="span"
                                      sx={{
                                        fontWeight: 'bold',
                                        fontSize: '0.775rem',
                                        display: 'block',
                                        mb: 0.5
                                      }}
                                    >
                                      {`Step No : ${getStepNumber(conditionStep.uid)} - ${conditionStep.title}`}
                                    </Typography>
                                  )}
                                  <Typography variant="body2" color="text.secondary">
                                    {toSentenceCase(field?.question || condition.field)} {condition.expression} {toSentenceCase(condition.value)}
                                  </Typography>
                                  {Boolean(
                                    field?.options?.length &&
                                    condition.value &&
                                    condition.value !== 'qs-undefined' &&
                                    !field.options.includes(condition.value)
                                  ) && (
                                    <Typography
                                      variant="caption"
                                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'warning.main', mt: 0.25 }}
                                      title={`"${condition.value}" is no longer an option on this field — this condition can never match.`}
                                    >
                                      <ErrorOutlineIcon sx={{ fontSize: '0.9rem' }} />
                                      Invalid — option removed
                                    </Typography>
                                  )}
                                  {showOperator && conditionIndex < conditionsToDisplay.length - 1 && (
                                    <Typography
                                      variant="caption"
                                      color="green"
                                      sx={{ display: 'block', mt: 0.5 }}
                                    >
                                      {operatorLabel}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            });
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top', padding: 0 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'left' }}>
                          {/* Show Step Block Till label for step block rules */}
                          {rule.isStepRule && (
                            <span style={{ color: '#1b9cec', fontWeight: 500 }}>
                              Step Block Till
                            </span>
                          )}
                          
                          {/* Show child transaction info if enabled */}
                          {!rule.isStepRule && rule.childTxnEnabled && rule.childTxnTeam && (() => {
                           
                            return (
                              <span style={{ color: '#1976d2', fontWeight: 500 }}>
                                Child Txn: {rule.childTxnTeamName || 'Unknown Process'}
                              </span>
                            );
                          })()}
                          
                          {/* Show main success action - only for non-step-block rules and when no child transaction */}
                          {!rule.isStepRule && !rule.childTxnEnabled && rule.successAction && rule.successAction !== 'N/A' && (
                            <span style={{ color: '#4caf50', fontWeight: 500 }}>
                              Go To <br /> Step No : {(() => {
                                const successStep = flowSteps.find(step => step.uid === rule.successAction);
                                if (successStep) {
                                  const stepIndex = flowSteps.findIndex(s => s.uid === successStep.uid);
                                  return (
                                    <>
                                      {stepIndex + 1}
                                      <br />
                                      {successStep.title}
                                    </>
                                  );
                                }
                                return 'Unknown';
                              })()}
                            </span>
                          )}
                          
                          {/* Show looping info only for non-step-block rules and when no child transaction */}
                          {!rule.isStepRule && !rule.childTxnEnabled && rule.enableLooping && (
                            <>
                              <br />
                              <span style={{ color: '#1976d2', fontWeight: 500 }}>
                                Go To <br /> Step No : {(() => {
                                  // Handle both object and string cases for loopBackTo
                                  const loopBackTo = rule.loopBackTo as any;
                                  let stepUid = '';
                                  if (typeof loopBackTo === 'object' && loopBackTo?.uid) {
                                    stepUid = loopBackTo.uid;
                                  } else if (typeof loopBackTo === 'string') {
                                    stepUid = loopBackTo;
                                  }
                                  
                                  if (stepUid) {
                                    const step = flowSteps.find(s => s.uid === stepUid);
                                    if (step) {
                                      const stepIndex = flowSteps.findIndex(s => s.uid === step.uid);
                                      return (
                                        <>
                                          {stepIndex + 1}
                                          <br />
                                          {step.title}
                                        </>
                                      );
                                    }
                                  }
                                  return 'Unknown';
                                })()}
                              </span>
                            </>
                          )}
                        </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', verticalAlign: 'top' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditRule(rule)}
                            sx={{ color: 'primary.main' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteRule(rule.id, rule.title)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Rule Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, minHeight: '80vh', maxHeight: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {isNewRule ? 'Add rule' : 'Edit rule'}
            </Typography>
          
            <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', gap: 1 , pt:1}}>
               <FormControl size="small" sx={{ flex: '1 1 150px', minWidth: '150px' }}>
                <InputLabel>Rule Type</InputLabel>
                <Select
                  value={ruleType}
                  onChange={(e) => setRuleType(e.target.value as 'standard' | 'stepBlock')}
                  label="Rule Type"
                  disabled={!isNewRule}
                >
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="stepBlock">block Step</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Rule Name"
                value={formData.title}
                onChange={handleInputChange('title')}
                variant="outlined"
                size="small"
                sx={{ flex: '1 1 200px', minWidth: '200px' }}
              /> 
              <FormControl size="small" sx={{  flex: '1 1 150px', minWidth: '150px' }}>
                <InputLabel>Rule Apply On </InputLabel>
                <Select
                  value={formData.currentNodeUid}
                  onChange={handleInputChange('currentNodeUid')}
                  label="CurrentStep Name"
                  disabled={loading || !isNewRule}
                >
                  {flowSteps.map((step, stepIdx) => (
                    <MenuItem key={step.uid} value={step.uid}>
                      {getStepLabel(step, stepIdx)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ py: 2, px:3, height: '60vh', overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, minHeight: 'fit-content' }}>
            {/* First Row - Rule condition fields */}
             
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {formData.conditions.map((condition, index) => {
                const conditionFields = getFieldsForStep(condition.step);
                const selectedField = conditionFields.find(field =>
                  (field.uid || field.question) === condition.field
                );

                return (
                  <Box
                    key={`${condition.step || 'condition'}-${index}`}
                    sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}
                  >
                    {/* Step Name with Operator Badge */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flex: '1 1 150px', minWidth: '150px', position: 'relative' }}>
                    {/* Operator Badge - Show for conditions after the first one */}
                    {index > 0 && formData.conditionOperator && (
                      <Typography sx={{ color: '#4caf50', fontWeight: 600, fontSize: '0.875rem' }}>
                        {formData.conditionOperator.toUpperCase()}
                      </Typography>
                    )}
                      <FormControl size="small" sx={{ flex: 1 }}>
                        <InputLabel>Step Name</InputLabel>
                        <Select
                          value={condition.step}
                          onChange={(e) => handleConditionStepChange(index, e.target.value)}
                          label="Step Name"
                          disabled={loading}
                        >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {flowSteps.map((step, stepIdx) => (
                          <MenuItem key={step.uid} value={step.uid}>
                            {getStepLabel(step, stepIdx)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    </Box>

                    <FormControl size="small" sx={{ flex: '1 1 120px', minWidth: '120px' }}>
                      <InputLabel>Field</InputLabel>
                      <Select
                        value={condition.field}
                        onChange={(e) => handleConditionFieldChange(index, e.target.value)}
                        label="Field"
                        disabled={conditionFields.length === 0}
                      >
                        {conditionFields.map((field, fieldIndex) => (
                          <MenuItem key={field.uid || fieldIndex} value={field.uid || field.question}>
                            {field.question}
                          </MenuItem>
                        ))}
                        {conditionFields.length === 0 && (
                          <MenuItem disabled>
                            {condition.step ? 'No fields available for this step' : 'Select a step first'}
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ flex: '1 1 120px', minWidth: '120px' }}>
                      <InputLabel>Expression</InputLabel>
                      <Select
                        value={condition.expression}
                        onChange={(e) => updateConditionAtIndex(index, { expression: e.target.value })}
                        label="Expression"
                      >
                        {EXPRESSIONS.map((expr) => (
                          <MenuItem key={expr.value} value={expr.value}>
                            {expr.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Conditional Value Field - Select dropdown if field has options, otherwise TextField */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '1 1 140px', minWidth: '140px' }}>
                      {selectedField && selectedField.options && selectedField.options.length > 0 ? (
                        <FormControl size="small" sx={{ flex: 1, minWidth: '120px' }}>
                          <InputLabel>Value</InputLabel>
                          <Select
                            value={condition.value}
                            onChange={(e) => updateConditionAtIndex(index, { value: e.target.value })}
                            label="Value"
                          >
                            <MenuItem value="qs-undefined">No Value</MenuItem>
                            {selectedField.options.map((option, optionIndex) => (
                              <MenuItem key={optionIndex} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          label="Value"
                          value={condition.value}
                          onChange={(e) => updateConditionAtIndex(index, { value: e.target.value })}
                          variant="outlined"
                          size="small"
                          sx={{ flex: 1, minWidth: '120px' }}
                        />
                      )}

                      <IconButton
                        size="small"
                        onClick={() => handleRemoveCondition(index)}
                        disabled={formData.conditions.length <= 1}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant={formData.conditionOperator === 'and' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handleConditionOperatorSelect('and')}
                  sx={{ borderRadius: 2 }}
                >
                  AND
                </Button>
                <Button
                  variant={formData.conditionOperator === 'or' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handleConditionOperatorSelect('or')}
                  sx={{ borderRadius: 2 }}
                >
                  OR
                </Button>
              </Box>

              {formData.conditionOperator && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddCondition}
                  sx={{ alignSelf: 'flex-start', borderRadius: 1, px: 2 }}
                >
                  Add Condition
                </Button>
              )}
            </Box>

            {/* Second Row - Actions Section with Three-Column Layout */}
            <Box sx={{ display: (isStepRule || ruleType === 'stepBlock') ? 'none' : 'block' }}>
              <Typography   variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
                Action  
              </Typography  >
              
              {/* Main Actions Container with Three-Column Layout */}
              <Box sx={{ display: 'flex', gap: 3 }}>
                
                {/* Left Box - Success/Failure Actions */}
                <Box sx={{ 
                  flex: 1,
                  border: '1px solid', 
                  borderColor: 'divider', 
                  borderRadius: 2, 
                  p: 2, 
                  backgroundColor: 'background.paper' ,
                  display:'none' //hide it for now if else action
                }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                    📋 Step Actions
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Success Action Dropdown */}
                    <FormControl size="small" fullWidth>
                      <InputLabel>On Success</InputLabel>
                      <Select
                        value={formData.successAction}
                        onChange={handleInputChange('successAction')}
                        label="On Success"
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {/* Add existing steps first */}
                        {flowSteps.map((step, stepIdx) => (
                          <MenuItem key={`existing-success-${step.uid}`} value={step.uid}>
                            {getStepLabel(step, stepIdx)}
                          </MenuItem>
                        ))}
                        <Divider />
                        {/* Then add step types for creating new steps */}
                        {Object.values(StepType).map((stepType) => (
                          <MenuItem key={`new-success-${stepType}`} value={stepType}>
                          Create: {stepType.toLowerCase().replace(/^\w/, (c) => c.toUpperCase())}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    {/* Failure Action Dropdown */}
                    <FormControl size="small" fullWidth>
                      <InputLabel>On Failure</InputLabel>
                      <Select
                        value={formData.failureAction}
                        onChange={handleInputChange('failureAction')}
                        label="On Failure"
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {/* Add existing steps first */}
                        {flowSteps.map((step, stepIdx) => (
                          <MenuItem key={`existing-failure-${step.uid}`} value={step.uid}>
                            {getStepLabel(step, stepIdx)}
                          </MenuItem>
                        ))}
                        <Divider />
                        {/* Then add step types for creating new steps */}
                        {Object.values(StepType).map((stepType) => (
                          <MenuItem key={`new-failure-${stepType}`} value={stepType}>
                          Create: {stepType.toLowerCase().replace(/^\w/, (c) => c.toUpperCase())}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

             

                {/* Right Box - Loop Configuration */}
                <Box sx={{ 
                   flex: (isStepRule || ruleType === 'stepBlock') ? 1 : '0 0 50%',  // Full width when actions hidden, 50% when visible
                  border: '1px solid', 
                  borderColor: formData.childTxnEnabled ? 'grey.300' : 'divider', 
                  borderRadius: 2, 
                  p: 2, 
                  backgroundColor: formData.childTxnEnabled ? 'grey.50' : 'background.paper',
                  display: (isStepRule || ruleType === 'stepBlock') ? 'none' : 'block',  // Hide when step block rule
                  opacity: formData.childTxnEnabled ? 0.5 : 1,
                  pointerEvents: formData.childTxnEnabled ? 'none' : 'auto'
                }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: formData.childTxnEnabled ? 'text.disabled' : 'primary.main' }}>
                   Go To  Configuration {formData.childTxnEnabled && '(Disabled when Child Transaction is enabled)'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Enable Loop Control */}
                    <FormControl size="small" fullWidth sx={{display:'none'}}>
                      <InputLabel>Enable Go to </InputLabel>
                      <Select
                        value={formData.enableLooping ? 'yes' : 'no'}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          enableLooping: e.target.value === 'yes',
                          maxLoopIterations: e.target.value === 'yes' ? 500000000 : formData.maxLoopIterations,  // Always set to 5000000 when enabled
                          childTxnEnabled: e.target.value === 'yes' ? false : formData.childTxnEnabled, // Disable child txn when looping is enabled
                          childTxnTeam: e.target.value === 'yes' ? '' : formData.childTxnTeam // Clear child txn team when looping is enabled
                        })}
                        label="Enable Loop"
                      >
                        <MenuItem value="no">No</MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Loop Controls - Only show when enableLooping is true */}
                    {formData.enableLooping && (
                      <>
                        {/* <TextField
                          label="Max Loop Iterations"
                          type="number"
                          value={formData.maxLoopIterations}
                          onChange={(e) => setFormData({ ...formData, maxLoopIterations: parseInt(e.target.value) || 1 })}
                          variant="outlined"
                          size="small"
                          fullWidth
                          inputProps={{ min: 1 }}
                          
                        /> */}
                        
                        <FormControl size="small" fullWidth>
                          <InputLabel>Go To Step</InputLabel>
                          <Select
                            value={formData.loopBackTo}
                            onChange={handleInputChange('loopBackTo')}
                            label="Go To Step"
                          >
                            {flowSteps.map((step, stepIdx) => (
                              <MenuItem key={step.uid} value={step.uid}>
                                {getStepLabel(step, stepIdx)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Iteration marking — only visible when a Go To step is selected */}
                        {formData.loopBackTo && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
                            {/* Which roles' steps this rule may add back or cancel. The step
                                range comes from the flow graph; this decides whose work inside
                                that range the rule is allowed to touch. */}
                            <FormControl size="small" fullWidth>
                              <InputLabel>Role this rule runs for </InputLabel>
                              <Select
                                value={formData.loopActorScopeMode || 'ALL'}
                                label="Steps this rule moves"
                                onChange={(e) => setFormData({
                                  ...formData,
                                  loopActorScopeMode: e.target.value as 'ALL' | 'TRIGGER_STEP' | 'SELECTED',
                                  loopActorRoles: [],
                                })}
                              >
                                <MenuItem value="ALL">All roles </MenuItem>
                                <MenuItem value="TRIGGER_STEP">Roles of rule apply on </MenuItem>
                                <MenuItem value="SELECTED">Specific role</MenuItem>
                              </Select>
                            </FormControl>

                            {formData.loopActorScopeMode === 'SELECTED' && (
                              <FormControl size="small" fullWidth>
                                <InputLabel>Roles</InputLabel>
                                <Select
                                  multiple
                                  value={formData.loopActorRoles || []}
                                  label="Roles"
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    loopActorRoles: typeof e.target.value === 'string'
                                      ? e.target.value.split(',')
                                      : (e.target.value as string[]),
                                  })}
                                  renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {(selected as string[]).map((id) => (
                                        <Chip
                                          key={id}
                                          size="small"
                                          label={roleOptions.find((r) => r.id === id)?.name || id}
                                        />
                                      ))}
                                    </Box>
                                  )}
                                >
                                  {loadingRoles ? (
                                    <MenuItem disabled>Loading roles…</MenuItem>
                                  ) : roleOptions.length === 0 ? (
                                    <MenuItem disabled>No roles found</MenuItem>
                                  ) : (
                                    roleOptions.map((r) => (
                                      <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                                    ))
                                  )}
                                </Select>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                  Selecting none falls back to all roles.
                                </Typography>
                              </FormControl>
                            )}

                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={formData.markIteration || false}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                                    ...formData,
                                    markIteration: e.target.checked,
                                    loopIterationLabelStep: '',
                                    loopIterationLabelField: '',
                                    firstIterationLabelStep: '',
                                    firstIterationLabelField: '',
                                  })}
                                />
                              }
                              label={<Typography variant="body2" fontWeight={500}>Mark iterations in history</Typography>}
                            />

                            {formData.markIteration && (
                              <>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Label from Step</InputLabel>
                                  <Select
                                    value={formData.loopIterationLabelStep || ''}
                                    onChange={(e) => setFormData({
                                      ...formData,
                                      loopIterationLabelStep: e.target.value as string,
                                      loopIterationLabelField: '',
                                    })}
                                    label="Label from Step"
                                  >
                                    {flowSteps.map((step, stepIdx) => (
                                      <MenuItem key={step.uid} value={step.uid}>
                                        {getStepLabel(step, stepIdx)}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>

                                {formData.loopIterationLabelStep && (
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Label Field</InputLabel>
                                    <Select
                                      value={formData.loopIterationLabelField || ''}
                                      onChange={(e) => setFormData({
                                        ...formData,
                                        loopIterationLabelField: e.target.value as string,
                                      })}
                                      label="Label Field"
                                    >
                                      {(flowSteps.find(s => s.uid === formData.loopIterationLabelStep)?.component?.fields || []).map((f: any) => (
                                        <MenuItem key={f.field || f.question} value={f.field || f.question}>
                                          {f.question}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}

                                {/* First-leg (iteration 1) label source. The first leg runs
                                    before the loop ever fires, so its label cannot be carried
                                    forward — read it directly from this step+field. Leave empty
                                    to reuse the loop label source above. */}
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                  First-leg label (before first loop) — optional
                                </Typography>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>First-leg label from Step</InputLabel>
                                  <Select
                                    value={formData.firstIterationLabelStep || ''}
                                    onChange={(e) => setFormData({
                                      ...formData,
                                      firstIterationLabelStep: e.target.value as string,
                                      firstIterationLabelField: '',
                                    })}
                                    label="First-leg label from Step"
                                  >
                                    {flowSteps.map((step, stepIdx) => (
                                      <MenuItem key={step.uid} value={step.uid}>
                                        {getStepLabel(step, stepIdx)}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>

                                {formData.firstIterationLabelStep && (
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>First-leg Label Field</InputLabel>
                                    <Select
                                      value={formData.firstIterationLabelField || ''}
                                      onChange={(e) => setFormData({
                                        ...formData,
                                        firstIterationLabelField: e.target.value as string,
                                      })}
                                      label="First-leg Label Field"
                                    >
                                      {(flowSteps.find(s => s.uid === formData.firstIterationLabelStep)?.component?.fields || []).map((f: any) => (
                                        <MenuItem key={f.field || f.question} value={f.field || f.question}>
                                          {f.question}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}
                              </>
                            )}
                          </Box>
                        )}
                      </>
                    )}
                    
                    {/* Helper text when loop is disabled */}
                    {!formData.enableLooping && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Enable looping for conditional retries
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Child Transaction Configuration Box */}
                <Box sx={{ 
                  flex: '0 0 50%',
                  border: '1px solid', 
                  borderColor: 'divider', 
                  borderRadius: 2, 
                  p: 2, 
                  backgroundColor: 'background.paper',
                  display: (isStepRule || ruleType === 'stepBlock') ? 'none' : 'block'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 3, flexWrap: 'nowrap', alignContent: 'flex-start' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.childTxnEnabled}
                          onChange={(e) => {
                            const isEnabled = e.target.checked; 
                            
                            setFormData({ 
                              ...formData, 
                              childTxnEnabled: isEnabled,
                              // Only clear team if disabling, keep it if enabling
                              childTxnTeam: isEnabled ? formData.childTxnTeam : '',
                              enableLooping: isEnabled ? false : formData.enableLooping, // Disable looping when child txn is enabled
                              childData: isEnabled ? formData.childData : false
                            }); 
                          }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          Child Transaction
                        </Typography>
                      }
                      sx={{ m: 0 }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                      <Checkbox
                        checked={formData.childData}
                        onChange={(e) => setFormData({ ...formData, childData: e.target.checked })}
                        disabled={!formData.childTxnEnabled}
                        size="small" 
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', lineHeight: 1.5,  mt:1}}>
                          Copy parent data into child
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          It works correctly only when the selected process matches the parent process.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Show team dropdown when child transaction is enabled */}
                    {formData.childTxnEnabled && (
                      <FormControl size="small" fullWidth>
                        <InputLabel>Select Process</InputLabel>
                        <Select
                          value={formData.childTxnTeam}
                          onChange={handleInputChange('childTxnTeam')}
                          label="Select Process"
                          disabled={loadingTeams}
                        >
                          {loadingTeams ? (
                            <MenuItem disabled>Loading processes...</MenuItem>
                          ) : teamOptions.length === 0 ? (
                            <MenuItem disabled>No processes available</MenuItem>
                          ) : (
                            teamOptions.map((team) => (
                              <MenuItem key={team.uid} value={team.uid}>
                                {team.name}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    )}
                    
                    {/* Helper text when child transaction is disabled */}
                    {!formData.childTxnEnabled && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Enable to create a child transaction in another process
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleCloseEditDialog}
            color="inherit"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRule}
            variant="contained"
            disabled={
              savingRule ||
              // For new rules, at minimum require title
              (isNewRule && !formData.title) ||
              // For updates, no required fields (everything optional)
              // Loop validation only when loop is enabled AND rule type is standard (not step block) AND child transaction is NOT enabled
              (ruleType === 'standard' && !formData.childTxnEnabled && formData.enableLooping && (!formData.loopBackTo || formData.maxLoopIterations < 1)) ||
              // Child transaction validation - if enabled, require team selection
              (formData.childTxnEnabled && !formData.childTxnTeam)
            }
            startIcon={savingRule ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ borderRadius: 1 }}
          >
            {savingRule ? 'Saving & Applying...' : (isNewRule ? 'Add Rule' : 'Save Changes')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConditionPopover;
